import React, { useState, useEffect } from 'react';
import {
  updateAdminMessage,
  clearAdminMessage,
  getStatusListener,
  getPrivateActivityListener,
  getOutageHistoryListener,
  getAdminMessageHistoryListener,
  signInWithGoogle,
  signOut,
  onAuthStateChanged
} from '../services/firebaseService';
import '../styles/AdminPanel.css';
import '../styles/ActivityPanel.css';

// Use the same UID that is authorized in firestore.rules.
const ADMIN_UID = 'H8oiau8kh6WVJtsDSRchULRQCjH3';

const minutes = (milliseconds) => Math.max(0, Math.floor((milliseconds || 0) / 60000));

const describeSession = (session) => {
  const state = session.state
    ? session.state.charAt(0).toUpperCase() + session.state.slice(1)
    : 'Unknown';
  const position = minutes(session.positionMs);
  if (!session.durationMs || session.mediaType === 'live') {
    return session.mediaType === 'live' ? `${state} — Live` : `${state} — ${position} minutes in`;
  }
  const remaining = Math.max(0, Math.ceil((session.durationMs - session.positionMs) / 60000));
  return `${state} — ${position} minutes in · ${remaining} minutes remaining`;
};

const timestampValue = (value) => value ? new Date(value).getTime() : NaN;

export default function AdminPanel() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [autoExpire, setAutoExpire] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastHeartbeat, setLastHeartbeat] = useState(null);
  const [activity, setActivity] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [copyStatus, setCopyStatus] = useState('');
  const [outages, setOutages] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (authenticatedUser) => {
      if (authenticatedUser && authenticatedUser.uid !== ADMIN_UID) {
        setUser(null);
        setError('This Google account is not authorized to access the admin panel.');
        await signOut();
        setAuthLoading(false);
        return;
      }
      setUser(authenticatedUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const unsubscribeStatus = getStatusListener((status) => {
      setCurrentMessage(status.adminMessage || '');
      setLastHeartbeat(status.lastHeartbeat);
    });
    const unsubscribeActivity = getPrivateActivityListener((snapshot, listenerError) => {
      setActivity(snapshot);
      if (listenerError) setError('Unable to load private activity. Check Firestore rules.');
    });
    const unsubscribeOutages = getOutageHistoryListener((history, listenerError) => {
      setOutages(history);
      if (listenerError) setError('Unable to load outage history. Check Firestore rules.');
    });
    const unsubscribeMessages = getAdminMessageHistoryListener((history, listenerError) => {
      setMessageHistory(history);
      if (listenerError) setError('Unable to load status-message history. Check Firestore rules.');
    });
    return () => {
      unsubscribeStatus();
      unsubscribeActivity();
      unsubscribeOutages();
      unsubscribeMessages();
    };
  }, [user]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const statusAge = lastHeartbeat ? now - new Date(lastHeartbeat).getTime() : Infinity;
  const isPotentialOutage = statusAge >= 90 * 1000;

  const formatDuration = (totalSeconds) => {
    const seconds = Math.max(0, Math.round(totalSeconds));
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    const parts = [];
    if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    if (mins) parts.push(`${mins} minute${mins === 1 ? '' : 's'}`);
    if (remainingSeconds || parts.length === 0) {
      parts.push(`${remainingSeconds} second${remainingSeconds === 1 ? '' : 's'}`);
    }
    return parts.join(' ');
  };

  const messagesForOutage = (outage) => {
    const start = timestampValue(outage.startedAt);
    const end = timestampValue(outage.endedAt);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
    return messageHistory
      .filter((item) => {
        const postedAt = timestampValue(item.postedAt);
        return Number.isFinite(postedAt) && postedAt >= start && postedAt <= end;
      })
      .sort((a, b) => timestampValue(a.postedAt) - timestampValue(b.postedAt));
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await signInWithGoogle();
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') setError('Google sign-in failed: ' + err.message);
    }
    setLoading(false);
  };

  const handlePostMessage = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await updateAdminMessage(message, autoExpire);
      setMessage('');
      setAutoExpire(false);
    } catch (err) {
      setError(err.code === 'permission-denied' ? 'This account is not authorized to post messages.' : err.message);
    }
    setLoading(false);
  };

  const handleClearMessage = async () => {
    setLoading(true);
    setError('');
    try {
      await clearAdminMessage();
    } catch (err) {
      setError(err.code === 'permission-denied' ? 'This account is not authorized to clear messages.' : err.message);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    setLoading(true);
    await signOut();
    setMessage('');
    setAutoExpire(false);
    setActivity(null);
    setLoading(false);
  };

  const handleCopy = async () => {
    if (!activity?.sessions?.length) return;
    const lines = activity.sessions.map((session) =>
      `${session.user} — ${session.title} — ${describeSession(session).replace(' · ', ', ')}`
    );
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopyStatus('Copied');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch {
      setCopyStatus('Copy failed');
    }
  };

  const handleBack = () => { window.location.href = '/'; };

  if (authLoading) return <div className="admin-container"><div className="login-card"><h1>Plex Status Admin</h1><p className="auth-status">Checking sign-in…</p></div></div>;

  if (!user) {
    return (
      <div className="admin-container"><div className="login-card">
        <h1>Plex Status Admin</h1>
        <p className="auth-status">Sign in with your authorized Google account.</p>
        <button onClick={handleLogin} disabled={loading} className="google-signin-btn">{loading ? 'Signing in…' : 'Sign in with Google'}</button>
        <button onClick={handleBack} className="back-btn login-back-btn">← Back</button>
        {error && <p className="error">{error}</p>}
      </div></div>
    );
  }

  return (
    <div className="admin-container"><div className="admin-card">
      <div className="admin-header">
        <h1>Plex Status Admin</h1>
        <div className="admin-buttons">
          <button onClick={handleBack} className="back-btn">← Back</button>
          <button onClick={handleLogout} className="logout-btn">Log Out</button>
        </div>
      </div>
      <p className="signed-in-user">Signed in as {user.email}</p>

      <section className={`activity-panel ${isPotentialOutage ? 'activity-outage' : ''}`}>
        <div className="activity-heading">
          <div>
            <h2>{isPotentialOutage ? 'Last known activity before outage' : 'Current Activity'}</h2>
            <p>{activity?.capturedAt ? `Captured ${new Date(activity.capturedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'No activity snapshot received yet'}</p>
          </div>
          {activity?.sessions?.length > 0 && <button onClick={handleCopy} className="copy-btn">{copyStatus || 'Copy viewer list'}</button>}
        </div>

        {!activity ? <p className="activity-empty">Waiting for Tautulli activity…</p>
          : activity.sessions.length === 0 ? <p className="activity-empty">Nobody was watching at the last check.</p>
          : <div className="session-list">{activity.sessions.map((session, index) => (
            <div className="session-card" key={`${session.user}-${session.title}-${index}`}>
              <strong>{session.user}</strong>
              <span className="session-title">{session.title}</span>
              <span className="session-progress">{describeSession(session)}</span>
            </div>
          ))}</div>}
      </section>

      <div className="section">
        <h2>Post Status Message</h2>
        <p className="hint">Post a message when Plex is down (e.g., "Maintenance until 11pm")</p>
        <form onSubmit={handlePostMessage}>
          <textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Enter status message..." rows="4" />
          <div className="message-actions">
            <button type="submit" disabled={loading || !message}>{loading ? 'Posting...' : 'Post Message'}</button>
            <label className="auto-expire-option">
              <input type="checkbox" checked={autoExpire} onChange={(event) => setAutoExpire(event.target.checked)} />
              <span>Clear after 4 successful checks</span>
            </label>
          </div>
        </form>
      </div>

      <div className="section outage-history">
        <h2>Outage History</h2>
        {outages.length === 0 ? (
          <p className="history-empty">No qualifying outages recorded.</p>
        ) : (
          <div className="outage-list">
            {outages.map((outage) => {
              const relatedMessages = messagesForOutage(outage);
              const viewersAtStart = outage.viewersAtStart;
              const startDate = new Date(outage.startedAt);
              const endDate = new Date(outage.endedAt);
              const dateOptions = { day: 'numeric', month: 'long', year: 'numeric' };
              const startDateLabel = startDate.toLocaleDateString([], dateOptions);
              const endDateLabel = endDate.toLocaleDateString([], dateOptions);
              const dateLabel = startDate.toDateString() === endDate.toDateString()
                ? startDateLabel
                : `${startDateLabel} – ${endDateLabel}`;
              return (
                <div className="outage-row" key={outage.id}>
                  <div className="outage-summary">
                    <div className="outage-time">
                      <strong>{dateLabel}</strong>
                      <span>{startDate.toLocaleTimeString([], {
                        hour: 'numeric', minute: '2-digit'
                      })} – {endDate.toLocaleTimeString([], {
                        hour: 'numeric', minute: '2-digit'
                      })}</span>
                    </div>
                    <span className="outage-duration">{formatDuration(outage.durationSeconds)}</span>
                  </div>
                  {Array.isArray(viewersAtStart) && (
                    <div className="outage-viewers">
                      <span className="outage-viewers-label">Watching when outage started</span>
                      <span>{viewersAtStart.length > 0 ? viewersAtStart.join(', ') : 'Nobody was watching.'}</span>
                    </div>
                  )}
                  {relatedMessages.length > 0 && (
                    <div className="outage-messages">
                      <span className="outage-messages-label">Messages posted during outage</span>
                      {relatedMessages.map((item) => (
                        <div className="outage-message" key={item.id}>
                          <time>{new Date(item.postedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</time>
                          <span>{item.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {currentMessage && <div className="section"><h2>Current Message</h2><div className="message-display"><p>{currentMessage}</p></div><button onClick={handleClearMessage} disabled={loading} className="clear-btn">Clear Message</button></div>}
      {error && <p className="error">{error}</p>}
    </div></div>
  );
}

